import { BillData } from '../types/billing';

declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface PaymentData {
  amount: number; // Amount in rupees
  currency: string;
  billId: string;
  clientName: string;
  clientEmail: string;
  description: string;
}

export interface PaymentResult {
  success: boolean;
  paymentId?: string;
  error?: string;
  transactionId?: string;
}

// Razorpay configuration
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_your_key_here';

// Load Razorpay script dynamically
const loadRazorpayScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay script'));
    document.body.appendChild(script);
  });
};

export const paymentService = {
  // Create payment order (this would typically be done on backend)
  createPaymentOrder: async (paymentData: PaymentData) => {
    // In production, this should be called from your backend
    // For demo purposes, we'll create a mock order

    const orderData = {
      id: `order_${Date.now()}`,
      amount: Math.round(paymentData.amount * 100), // Convert to paisa
      currency: paymentData.currency,
      receipt: `rcpt_${paymentData.billId}`,
      notes: {
        billId: paymentData.billId,
        clientName: paymentData.clientName,
        clientEmail: paymentData.clientEmail
      }
    };

    return orderData;
  },

  // Initiate payment
  initiatePayment: async (paymentData: PaymentData): Promise<PaymentResult> => {
    try {
      // Load Razorpay script
      await loadRazorpayScript();

      // Create order (in production, call your backend API)
      const order = await paymentService.createPaymentOrder(paymentData);

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: paymentData.currency,
        name: 'AVIS Engineering & Consultancy',
        description: paymentData.description,
        image: '/logo61.png',
        order_id: order.id,
        handler: function (response: any) {
          // Payment successful callback
          console.log('Payment successful:', response);

          // Here you would verify the payment on your backend
          // and update the payment status in your database

          return {
            success: true,
            paymentId: response.razorpay_payment_id,
            transactionId: response.razorpay_order_id
          };
        },
        prefill: {
          name: paymentData.clientName,
          email: paymentData.clientEmail,
        },
        notes: {
          billId: paymentData.billId,
          address: 'AVIS Engineering & Consultancy'
        },
        theme: {
          color: '#2C4A6B'
        },
        modal: {
          ondismiss: function() {
            console.log('Payment modal dismissed');
            return { success: false, error: 'Payment cancelled by user' };
          }
        }
      };

      const rzp = new window.Razorpay(options);

      return new Promise((resolve) => {
        rzp.on('payment.success', (response: any) => {
          resolve({
            success: true,
            paymentId: response.razorpay_payment_id,
            transactionId: response.razorpay_order_id
          });
        });

        rzp.on('payment.error', (error: any) => {
          resolve({
            success: false,
            error: error.description || 'Payment failed'
          });
        });

        rzp.on('payment.cancel', () => {
          resolve({
            success: false,
            error: 'Payment cancelled by user'
          });
        });

        rzp.open();
      });

    } catch (error) {
      console.error('Payment initiation failed:', error);
      return {
        success: false,
        error: 'Failed to initiate payment. Please try again.'
      };
    }
  },

  // Verify payment (should be done on backend in production)
  verifyPayment: async (paymentId: string, orderId: string, signature: string): Promise<boolean> => {
    // In production, this verification should happen on your backend
    // using Razorpay's webhook or API verification
    try {
      // Mock verification for demo
      console.log('Verifying payment:', { paymentId, orderId, signature });
      return true;
    } catch (error) {
      console.error('Payment verification failed:', error);
      return false;
    }
  },

  // Get payment status
  getPaymentStatus: async (paymentId: string) => {
    // In production, check payment status from your backend
    // which would have stored the status from Razorpay webhooks
    return {
      status: 'completed',
      amount: 0,
      currency: 'INR',
      method: 'card',
      createdAt: new Date().toISOString()
    };
  }
};

// Utility function to create payment data from bill
export const createPaymentDataFromBill = (bill: BillData, clientEmail?: string): PaymentData => {
  return {
    amount: bill.totalAmount,
    currency: 'INR',
    billId: bill.id || '',
    clientName: bill.clientName,
    clientEmail: clientEmail || '',
    description: `Payment for Invoice ${bill.invoiceNumber} - ${bill.items.length > 0 ? bill.items[0].description : 'NDT Services'}`
  };
};


export default paymentService;
